import { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatSlotLabel, type Subject } from '@musicville/shared';
import { Icon } from '@/components/brand/Icon';
import { InstrumentStage } from '@/components/home/InstrumentStage';
import { Button } from '@/components/ui/Button';
import { useBookingStore } from '@/lib/booking-store';
import { subjectName, subjectText } from '@/lib/catalogue';
import { cn } from '@/lib/cn';
import { instrumentKind } from '@/lib/instruments';
import { createInstrumentSound, type InstrumentSound } from '@/lib/instrumentSound';
import { useAvailability, useSubjects } from '@/lib/queries';
import { useI18n } from '@/lib/i18n';

const HOVER_INTENT_MS = 140;

export function Hero(): React.JSX.Element {
  const { t } = useI18n();
  const { data } = useSubjects();
  const subjects = data?.filter((s) => s.slug !== 'fingerstyle');
  const [selected, setSelected] = useState<string | null>(null);
  const [sound, setSound] = useState<InstrumentSound | null>(null);
  const [soundOn, setSoundOn] = useState(false);
  const [demo, setDemo] = useState(0);
  const intent = useRef(0);

  const subject = subjects?.find((s) => s.slug === selected) ?? subjects?.[0];

  useEffect(() => () => sound?.dispose(), [sound]);
  useEffect(() => () => window.clearTimeout(intent.current), []);

  const toggleSound = (): void => {
    const engine = sound ?? createInstrumentSound();
    if (!sound) setSound(engine);
    engine.setEnabled(!soundOn);
    if (!soundOn) setDemo((n) => n + 1);
    setSoundOn(!soundOn);
  };

  const hover = (slug: string): void => {
    window.clearTimeout(intent.current);
    intent.current = window.setTimeout(() => setSelected(slug), HOVER_INTENT_MS);
  };

  return (
    <section className="mv-hero bg-midnight text-on-midnight">
      <div className="mv-container grid gap-10 py-14 md:py-16 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:gap-16 lg:py-20">
        <div className="flex flex-col items-start">
          <h1 className="max-w-[16ch] font-display text-[clamp(2.5rem,5vw,3.75rem)] font-light leading-[1.06] tracking-[-0.02em]">
            {t('hero.headline')
              .split(' ')
              .map((word, i) => (
                <Fragment key={i}>
                  <span className="mv-word">
                    <span style={{ animationDelay: `${0.1 + i * 0.07}s` }}>{word}</span>
                  </span>{' '}
                </Fragment>
              ))}
          </h1>

          <p
            className="mv-rise mt-8 text-body text-on-midnight-muted"
            style={{ animationDelay: '0.5s' }}
          >
            {t('picker.prompt')}
          </p>

          <ul className="mt-3 w-full">
            {subjects?.map((item, i) => (
              <li
                key={item.slug}
                className="mv-rise"
                style={{ animationDelay: `${0.55 + i * 0.05}s` }}
              >
                <button
                  type="button"
                  aria-pressed={item.slug === subject?.slug}
                  onClick={() => setSelected(item.slug)}
                  onMouseEnter={() => hover(item.slug)}
                  onMouseLeave={() => window.clearTimeout(intent.current)}
                  className={cn(
                    'mv-pick flex w-full items-baseline py-1.5 text-left transition-colors duration-300',
                    item.slug === subject?.slug
                      ? 'text-on-midnight'
                      : 'text-on-midnight-muted hover:text-on-midnight',
                  )}
                >
                  <span className="mv-pick-name font-display text-[clamp(1.75rem,3.2vw,2.5rem)] font-light leading-[1.15]">
                    {subjectName(t, item)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {subject && (
          <PickerStage
            subject={subject}
            sound={soundOn ? sound : null}
            soundOn={soundOn}
            demo={demo}
            onToggleSound={toggleSound}
          />
        )}
      </div>
    </section>
  );
}

function PickerStage({
  subject,
  sound,
  soundOn,
  demo,
  onToggleSound,
}: {
  subject: Subject;
  sound: InstrumentSound | null;
  soundOn: boolean;
  demo: number;
  onToggleSound: () => void;
}): React.JSX.Element {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const { setSubject, setSlot } = useBookingStore();
  const { data, isPending } = useAvailability({ subject: subject.slug, days: 7, type: 'TRIAL' });
  const next = data?.days.flatMap((day) => day.slots)[0];

  const book = (withSlot: boolean): void => {
    setSubject(subject.slug, subjectName(t, subject));
    if (withSlot && next) {
      setSlot(next);
      navigate('/booking/details');
    } else {
      navigate('/booking/teacher');
    }
  };

  return (
    <div
      className="mv-rise mv-stage flex flex-col rounded-lg border border-border-on-midnight bg-midnight-raised"
      style={{ animationDelay: '0.3s' }}
    >
      <div className="flex items-start justify-between gap-4 p-5">
        <p key={subject.slug} className="mv-rise max-w-[24rem] text-body text-on-midnight-muted">
          {subjectText(t, subject.slug, 'tagline', subject.tagline)}
        </p>
        <button
          type="button"
          aria-pressed={soundOn}
          onClick={onToggleSound}
          className="inline-flex h-11 shrink-0 items-center gap-2 rounded-md border border-border-on-midnight px-4 text-caption font-medium text-on-midnight transition-colors hover:border-on-midnight-muted"
        >
          <Icon name="headphones" size={16} />
          {soundOn ? t('picker.mute') : t('picker.listen')}
        </button>
      </div>

      <div className="relative min-h-0 flex-1">
        <InstrumentStage
          kind={instrumentKind(subject.slug, subject.icon)}
          sound={sound}
          demo={demo}
        />
      </div>

      <p className="px-5 pb-3 text-center text-caption text-on-midnight-muted">
        {t('picker.hint')}
      </p>

      <div className="m-2 flex flex-wrap items-center justify-between gap-4 rounded-md bg-surface p-4 text-primary">
        <div>
          <p className="flex items-center gap-2 text-caption font-medium text-secondary">
            <Icon name="clock" size={16} />
            {t('hero.nextTrial')}
          </p>
          <p className="mt-1.5 text-h4 font-semibold">
            {next ? formatSlotLabel(next.startsAt, locale) : isPending ? '…' : t('hero.noTrial')}
          </p>
          {next && (
            <p className="mt-1 text-caption text-tertiary">
              {t('picker.withTeacher', { teacher: next.teacherName })}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="tertiary" onClick={() => book(false)}>
            {t('picker.otherTimes')}
          </Button>
          <Button trailingIcon="arrow-right" onClick={() => book(true)}>
            {next ? t('picker.bookSlot') : t('nav.bookTrial')}
          </Button>
        </div>
      </div>
    </div>
  );
}
